/*
 * NEXZ V2 — cart drawer remove-with-undo (Stage H, doc 10 "remove (text
 * link, with 5s undo toast rather than confirm dialog)").
 *
 * Horizon's native remove button already has no confirm dialog — it
 * removes immediately (component-cart-items.js#onLineItemRemove). The
 * one missing piece is the undo toast, which this adds without touching
 * that native removal flow at all: the click is observed (capture
 * phase, read-only — no preventDefault, no interception of the real
 * removal) purely to snapshot what's about to be removed, since the
 * line-key alone (all the native `shopify:cart:lines-update` remove
 * event actually carries — confirmed by reading component-cart-items.js
 * directly: `lines: [{ id: lineId, quantity: 0 }]`, no variant id, no
 * title) isn't enough to rebuild the line afterward. The variant id is
 * recovered from the cart line key itself — Shopify's documented
 * `{variant_id}:{properties_hash}` format — not a new data attribute.
 *
 * Undo re-adds via the same single-item Theme.routes.cart_add_url path
 * product-form.js itself uses.
 */
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

const UNDO_WINDOW_MS = 5000;

function buildToast() {
  let toast = document.querySelector('[data-nexz-undo-toast]');
  if (toast) return toast;
  toast = document.createElement('div');
  toast.setAttribute('data-nexz-undo-toast', '');
  toast.className = 'nexz-undo-toast';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.hidden = true;
  document.body.appendChild(toast);
  return toast;
}

function showUndoToast(snapshot) {
  const toast = buildToast();
  const label = window.NEXZ_CART_STRINGS?.removedItem || 'Removed';
  const undoLabel = window.NEXZ_CART_STRINGS?.undo || 'Undo';
  toast.innerHTML = '';
  toast.hidden = false;

  const message = document.createElement('span');
  message.className = 'nexz-undo-toast__message';
  message.textContent = snapshot.title ? `${label}: ${snapshot.title}` : label;

  const undoButton = document.createElement('button');
  undoButton.type = 'button';
  undoButton.className = 'nexz-undo-toast__action';
  undoButton.textContent = undoLabel;

  toast.appendChild(message);
  toast.appendChild(undoButton);

  let timeoutId = setTimeout(() => {
    toast.hidden = true;
  }, UNDO_WINDOW_MS);

  undoButton.addEventListener(
    'click',
    () => {
      clearTimeout(timeoutId);
      toast.hidden = true;
      restoreLine(snapshot);
    },
    { once: true }
  );
}

function restoreLine(snapshot) {
  if (!snapshot.variantId) return;

  const formData = new FormData();
  formData.append('id', snapshot.variantId);
  formData.append('quantity', String(snapshot.quantity || 1));

  const fetchCfg = fetchConfig('javascript', { body: formData });

  let resolvePromise, rejectPromise;
  const deferredPromise = new Promise((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });
  // cart-drawer.js attaches its own .catch to this promise, but only while a
  // cart-drawer component is mounted. Mark it handled here too so a failed
  // request on a flaky connection is reported by the UI below rather than
  // escaping as an unhandled rejection ("NetworkError: Load failed") into
  // error monitoring. Real consumers still receive the rejection.
  deferredPromise.catch(() => {});

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: `gid://shopify/ProductVariant/${snapshot.variantId}`, quantity: snapshot.quantity || 1 }],
      promise: deferredPromise,
    })
  );

  fetch(Theme.routes.cart_add_url, {
    ...fetchCfg,
    headers: { ...fetchCfg.headers, Accept: 'application/json' },
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        rejectPromise(new Error(response.message || 'Could not restore item.'));
        return;
      }
      resolvePromise({ cart: response, detail: { didError: false } });
      // The native cart-items-component listens for this same event and
      // re-renders via Section Rendering — no manual DOM patch needed.
      window.location.reload();
    })
    .catch((error) => rejectPromise(error));
}

function snapshotRow(row) {
  const key = row.dataset.key || '';
  const variantId = key.split(':')[0] || '';
  const titleEl = row.querySelector('.cart-items__title');
  const quantityInput = row.querySelector('.cart-items__quantity input[type="number"], .cart-items__quantity input');
  return {
    variantId,
    title: titleEl ? titleEl.textContent.trim() : '',
    quantity: quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1,
  };
}

document.addEventListener(
  'click',
  (event) => {
    const button = event.target.closest ? event.target.closest('.cart-items__remove') : null;
    if (!button) return;
    const row = button.closest('.cart-items__table-row');
    if (!row) return;
    // Read-only snapshot — does not prevent or delay the native removal.
    showUndoToast(snapshotRow(row));
  },
  { capture: true }
);
